import React from 'react';
import { colors, typography } from '../../../styles/theme';
import { FILTER_ACCENT, FILTER_ACCENT_33 } from './FilterPanel.styles';

const wrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
};

const unitHintStyle = {
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
};

const gridStyle = (columns) => ({
  display: 'grid',
  gridTemplateColumns: `minmax(72px, 0.9fr) repeat(${columns}, minmax(0, 1fr))`,
  gap: '6px',
  width: '100%',
  alignItems: 'center',
});

const headerCellStyle = {
  minWidth: 0,
  padding: '2px 2px',
  textAlign: 'center',
  fontSize: '10px',
  lineHeight: 1.2,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowLabelStyle = {
  minWidth: 0,
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  fontWeight: typography.fontWeight.medium,
  color: colors.textPanelDescription,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const cellStyle = ({ active = false, disabled = false } = {}) => ({
  width: '100%',
  minWidth: 0,
  height: '28px',
  boxSizing: 'border-box',
  padding: 0,
  borderRadius: '6px',
  border: `1px solid ${active ? FILTER_ACCENT : colors.border}`,
  backgroundColor: active ? FILTER_ACCENT_33 : 'rgba(255,255,255,0.04)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
  transition: 'all 0.12s ease',
});

/**
 * Matriz compacta: un header de bandas + una fila por variable (misma escala).
 * Cada celda togglea la banda correspondiente de esa fila.
 */
function FilterRangeMatrix({
  bandLabels = [],
  unit = null,
  rows = [],
  valuesByField = {},
  onChangeRow,
  disabled = false,
}) {
  const columns = bandLabels.length;

  const toggleCell = (row, option) => {
    const current = Array.isArray(valuesByField[row.field]) ? valuesByField[row.field] : [];
    const set = new Set(current);
    if (set.has(option.value)) set.delete(option.value);
    else set.add(option.value);
    const next = Array.from(set);
    onChangeRow?.(row.field, next.length ? next : null);
  };

  return (
    <div style={wrapStyle}>
      {unit ? <span style={unitHintStyle}>Bandas ({unit})</span> : null}
      <div style={gridStyle(columns)} aria-label="Bandas por variable">
        <div aria-hidden />
        {bandLabels.map((label) => (
          <div key={label} style={headerCellStyle} title={label}>
            {label}
          </div>
        ))}

        {rows.map((row) => {
          const selected = Array.isArray(valuesByField[row.field]) ? valuesByField[row.field] : [];
          return (
            <React.Fragment key={row.field}>
              <div style={rowLabelStyle} title={row.label}>{row.label}</div>
              {row.options.map((opt, idx) => {
                const active = selected.includes(opt.value);
                return (
                  <button
                    key={`${row.field}-${opt.key}`}
                    type="button"
                    aria-label={`${row.label}: ${bandLabels[idx] || opt.label}`}
                    aria-pressed={active}
                    style={cellStyle({ active, disabled })}
                    onClick={disabled ? undefined : () => toggleCell(row, opt)}
                    title={opt.label}
                  />
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default FilterRangeMatrix;
